import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, apiError, endSession } from "@/lib/api";

const AuthContext = createContext(null);

// Aktive Sitzungsprüfung: auch ohne Benutzeraktion darf keine angemeldete Oberfläche stehen
// bleiben, wenn die Sitzung serverseitig nicht mehr gültig ist.
const HEARTBEAT_MS = 60_000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=loading, null=logged out
  const checking = useRef(false);

  const check = useCallback(async (initial = false) => {
    if (checking.current) return;
    checking.current = true;
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      setUser(null);
      // Keine gültige Sitzung mehr: geschützte Inhalte dürfen nicht stehen bleiben.
      if (!initial && window.location.pathname !== "/login") endSession("expired");
    } finally {
      checking.current = false;
    }
  }, []);

  useEffect(() => {
    // Session is restored from the httpOnly cookie via /auth/me.
    check(true);
    const revalidate = () => { if (document.visibilityState === "visible") check(); };
    // Rückkehr aus dem Seiten-Cache (iOS Safari bfcache) und Tab-/Fensterwechsel.
    const onPageShow = (e) => { if (e.persisted) check(); };
    const timer = setInterval(revalidate, HEARTBEAT_MS);
    document.addEventListener("visibilitychange", revalidate);
    window.addEventListener("focus", revalidate);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", revalidate);
      window.removeEventListener("focus", revalidate);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [check]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = async (reason = "logout") => {
    // Zuerst den Zustand leeren: geschützte Ansichten verschwinden sofort, unabhängig davon,
    // wie lange der Server für das Beenden der Sitzung braucht.
    setUser(null);
    try { await api.post("/auth/logout"); }
    catch (error) { console.error("Logout request failed (session wird trotzdem lokal beendet):", apiError(error)); }
    // Harter Redirect + Reload: erzwingt frische Session-Prüfung (/auth/me) und leert jeglichen
    // In-Memory-/bfcache-Zustand. Verhindert, dass auf Mobil (iOS Safari) die alte, evtl. sensible
    // Oberfläche nach dem Abmelden sichtbar bleibt.
    window.location.replace(`/login?reason=${reason}`);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, revalidate: check }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { apiError };
